import {connect} from "react-redux";
import {AppState} from "../../RootReducer";
import {Dispatch} from "redux";
import {initDeposit, showPossibleDepositErrors, updateDepositAmount} from "../../payments/DepositActions";
import { CurrencyDefinition, DepositContext, TransferLimit } from "../../http/protocol";
import "./styles/Deposit.scss";
import { t } from "../../i18n";
import { BrandA } from "../buttons/buttons";
import { getDepositContext } from "../../selectors/payments-selectors";
import { getCurrencyDefinitions, getPreferredFiatExchange } from "../../selectors/settings-selectors";
import { hasFiatExchangeAmount, toFiatAmount } from "../../currency/currency-utils";
import { AmountInput } from "../../core/ui/molecules/AmountInput";
import TextField from "../../layout/TextField";

interface Props {
  update?: typeof updateDepositAmount;
  showPossibleErrors?: () => void;
  showErrors?: boolean;
  initDeposit?: () => void;
  currency?: string;
  amount?: string;
  amountValid?: boolean;
  handleOnClick?: () => void;
  userSettings?: { [index: string]: string };
  context?: DepositContext;
  currencyDefinitions: CurrencyDefinition[];
  preferredExchangeCurrency: string;
  transferLimits?: TransferLimit[];
}
export const ChooseAmount = (props: Props) => {
  const activeCurrency = props.currency;
  const amount = props.amount && props.amountValid ? `${props.amount} ${activeCurrency}` : "";
  const buttonText = t("cashier.buttonText").replace("|amount|", amount);
  let presetBaseAmount: number =
    (props.transferLimits &&
      props.transferLimits.filter((tl) => tl.currency === props.currency)[0]?.defaultDepositAmount) ||
    2;
  let userPrevDepositAmount: number | null = null;
  if (props.userSettings) {
    let preferred = props.userSettings["last_deposit_amount_" + activeCurrency];
    if (preferred && !isNaN(Number(preferred))) {
      userPrevDepositAmount = Number(preferred);
    }
  }

  if (userPrevDepositAmount != null && userPrevDepositAmount != 0) {
    if (userPrevDepositAmount == props.context?.minDeposit) {
      presetBaseAmount = userPrevDepositAmount * 2;
    } else if (userPrevDepositAmount === props.context?.maxDeposit) {
      presetBaseAmount = userPrevDepositAmount / 2;
    } else {
      presetBaseAmount = userPrevDepositAmount;
    }
  }
  let option1 = presetBaseAmount / 2;
  let option2 = presetBaseAmount;
  let option3 = presetBaseAmount * 2;

  const maxFractionalDigits = props.currencyDefinitions.find((cd) => cd.code === props.currency)?.fractionalDigits || 0;

  const formatAmount = (amount: number): string => {
    return amount.toFixed(maxFractionalDigits);
  };

  if (presetBaseAmount && props.context?.minDeposit && option1 < props.context.minDeposit) {
    option1 = props.context.minDeposit;
    if (option1 == option2) {
      option2 = option1 * 2;
      option3 = option1 * 3;
    }
  }
  option1 = parseFloat(formatAmount(option1));
  option2 = parseFloat(formatAmount(option2));
  option3 = parseFloat(formatAmount(option3));

  const presets = presetBaseAmount ? [option1.toString(), option2.toString(), option3.toString()] : [""];

  const showPossibleErrors = () => {
    props.showPossibleErrors && props.showPossibleErrors();
  };
  const currencyDefinition = props.currencyDefinitions.find((cd) => cd.code === props.currency);
  let validation = amountValid(props.amount, props.context, props.amountValid, currencyDefinition?.fractionalDigits);
  const errorText: string = validation.errorText;
  const validAmount: boolean = validation.valid;
  return (
    <div className={"choose-amount-container container"}>
      {presets && (
        <>
          <span className="row">{t("cashier.choose-amount")}</span>
          <div className="row">
            <AmountInput
              value={props.amount || ""}
              onChange={(value) => props.update && props.update(value)}
              currencySymbol={currencyDefinition?.prefixSymbol || currencyDefinition?.postfixSymbol}
              presets={presets.map(Number)}
              inputProps={{
                onBlur: () => showPossibleErrors(),
                inputMode: "numeric",
              }}
            />
          </div>
          {props.amount != "" && errorText && (
            <span className={errorText !== undefined && errorText !== "" ? `text visible error row` : "text"}>
              {errorText}
            </span>
          )}
        </>
      )}
      {props.amount &&
        props.amountValid &&
        props.currency &&
        hasFiatExchangeAmount(props.currency, props.preferredExchangeCurrency, props.currencyDefinitions) && (
          <div className={"row"}>
            <div className={"col-12 exchange-amount"}>
              {t("cashier.approximately")}{" "}
              {toFiatAmount(
                props.currency,
                parseFloat(props.amount),
                props.preferredExchangeCurrency,
                props.currencyDefinitions,
              )}
            </div>
          </div>
        )}

      <div className={"row button-wrapper"}>
        <div className={"col-12 button"}>
          {!props.handleOnClick && (
            <BrandA
              className={"btn btn-cta full-width large"}
              disabled={!validAmount}
              onClick={() => props.initDeposit && props.initDeposit()}
            >
              {buttonText}
            </BrandA>
          )}
          {props.handleOnClick && (
            <BrandA
              className={"btn btn-cta full-width large"}
              disabled={!validAmount}
              onClick={() => props.handleOnClick && props.handleOnClick()}
            >
              {buttonText}
            </BrandA>
          )}
        </div>
      </div>
    </div>
  );
};

const amountValid = (amount: string | undefined, context?: DepositContext, isAmountValid?: boolean, fractionalDigits?: number): { errorText: string; valid: boolean } => {
  let amountBig = Number(amount);
  const isAmountNaN = isNaN(amountBig);
  let minDeposit = context?.minDeposit;
  let maxDeposit = context?.maxDeposit;

  if (!isAmountValid && amount?.indexOf(",") != -1) {
    return { errorText: t("withdraw.dotInsteadOfCommaError"), valid: false };
  }
  if (isAmountNaN) {
    return { errorText: t("withdraw.onlyNumbersError"), valid: false };
  }
  if (amount && amount.includes(".")) {
    const decimalPart = amount.split(".")[1];
    if (decimalPart && fractionalDigits !== undefined && decimalPart.length > fractionalDigits) {
      return { errorText: t("deposit.amountErrorMaxDecimals") + ": " + fractionalDigits, valid: false };
    }
  }
  if (minDeposit && amountBig !== 0) {
    if (amountBig < minDeposit) {
      return { errorText: t("deposit.amountErrorMinDeposit") + ": " + minDeposit, valid: false };
    }
  }
  if (maxDeposit && amountBig !== 0) {
    if (amountBig > maxDeposit) {
      return { errorText: t("deposit.amountErrorMaxDeposit") + ": " + maxDeposit, valid: false };
    }
  }
  if (amountBig === 0) {
    return { errorText: t("store.bundles.noZeroAmount"), valid: false };
  } else if (amountBig >= 0) {
    return { errorText: "", valid: true };
  }
  if (!isAmountValid) {
    return { errorText: t("store.bundles.noZeroAmount"), valid: false };
  }

  return { errorText: "", valid: true };
};

const mapStateToProps = (app : AppState)=> {
    return {
      method: app.payments.deposit.activeMethod,
      currency: app.payments.deposit.activeCurrency,
      userSettings: app.user.user && app.user.user.userSettings,
      amount: app.payments.deposit.amount,
      amountValid: app.payments.deposit.amountValid,
      context: getDepositContext(app),
      currencyDefinitions: getCurrencyDefinitions(app),
      preferredExchangeCurrency: getPreferredFiatExchange(app),
      transferLimits: app.settings.settings?.transferLimits,
    };
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  update: (amount: string) => dispatch(updateDepositAmount(amount)),
  initDeposit: () => dispatch(initDeposit() as any),
});

export default connect(mapStateToProps,mapDispatchToProps)(ChooseAmount);

export function getIconsByMethod(code : string) {
    if(code == "MBKR") {
        return ["skrill"];
    } else if(code == "TRUSTLY") {
        return ["trustly"];
    } else if(code == "CARDPAY") {
        return ["visa", "mastercard", "amex"]
    } else if(code == "test-method") {
        return ["test"]
    } else if(code == "BTC-LIGHTNING") {
        return ["btc-ightning"]
    } else {
        return [];
    }
}